import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

/**
 * Dashboard left-rail navigation (card 050 — v2 IA).
 *
 * Replaces the verb-y v1 grouping (Build / Engage / Install /
 * Settings / You) with mode-based sections that match how
 * operators actually think:
 *
 *   - INBOX          Pinpoint help inbox.  Reactive work.
 *                    Always at the top.
 *
 *   - AUTHORING      Tours / Messages / Checklists / Knowledge base
 *                    / Themes.  The content the operator builds.
 *
 *   - INSIGHTS       Tour analytics / Content engagement / Help
 *                    feedback.  Read-only analytical surfaces.
 *
 *   - SETUP          Install / Sites / Authentication / Webhooks /
 *                    SDK version.  Collapses once familiar — they're
 *                    one-time configurations.
 *
 *   - TENANT ADMIN   Projects / Members / API keys / Email domains
 *                    / Tenant webhooks / Audit log / Billing /
 *                    Tenant settings.  Collapsed by default.
 *
 * Tenant + project pickers are NOT this component's concern (they
 * live in HeaderBar now).  The "You" section is gone — consolidated
 * into UserMenu on the header bar.
 *
 * Collapse model:
 *
 *   - Authoring + Insights are non-collapsing — they're the daily
 *     surface.  We render them as flat groups with a heading row.
 *   - Setup + Tenant admin are <details> elements, persisted per
 *     section to localStorage so an operator who likes them open
 *     keeps them open across sessions.  Setup defaults open
 *     (Setup is one-time but accessed during onboarding), Tenant
 *     admin defaults closed.
 *   - The whole rail no longer has a wide/rail toggle — the v2
 *     IA's mode-grouped sections were designed for the wide
 *     layout.  A future card can add a collapsed-rail mode if the
 *     ergonomics demand it; out of scope here.
 */
export function Sidebar({
  projectSlug = null,
  badges = {},
}) {
  const sections = useMemo(
    () => buildSections({ projectSlug, badges }),
    [projectSlug, badges],
  );

  return (
    <nav className="sb" aria-label="Primary">
      {/* Card spotlight-067 — global search pinned above Inbox.
          The input itself is a proxy: focusing it (or typing) hands
          control off to the <CommandPalette /> overlay which Shell
          mounts once.  We use a CustomEvent so the palette stays
          decoupled from the sidebar's render path. */}
      <SidebarSearchTrigger />
      {sections.map((section) => (
        <SectionView key={section.id} section={section} />
      ))}
      <style>{SIDEBAR_CSS}</style>
    </nav>
  );
}

function SidebarSearchTrigger() {
  const isMac = typeof navigator !== 'undefined'
    && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
  const hint = isMac ? '⌘K' : 'Ctrl K';

  function openPalette(query) {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('spotlight:cmdk-open', { detail: { query } }),
    );
  }

  return (
    <button
      type="button"
      className="sb__search"
      aria-label="Search Spotlight (Command-K)"
      onClick={() => openPalette('')}
      onKeyDown={(e) => {
        // Forward immediate typing into the palette — focusing the
        // sidebar input and typing "spo" should land you in the
        // palette pre-filled, not silently swallow the keystrokes.
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          openPalette(e.key);
        }
      }}
    >
      <span className="sb__search-icon" aria-hidden="true"><SearchIcon /></span>
      <span className="sb__search-label">Search</span>
      <kbd className="sb__search-kbd" aria-hidden="true">{hint}</kbd>
    </button>
  );
}

// ───────── section rendering

function SectionView({ section }) {
  if (section.kind === 'solo') {
    return (
      <div className="sb__solo-wrap">
        <SoloLink item={section.item} />
      </div>
    );
  }
  if (section.kind === 'flat') {
    // Card 100 — flat-group heading renders the section icon when one
    // is set, so Authoring / Insights match the visual weight of the
    // collapsible Setup / Tenant admin headings.
    // Card 103 — `scope` adds a left-edge brand accent to project-
    // scoped groups so operators can see at a glance which groups
    // change with the active project.
    const Icon = section.icon;
    const scopeClass = section.scope === 'project' ? ' sb__group--scope-project' : '';
    return (
      <div className={`sb__group sb__group--${section.id}${scopeClass}`}>
        <div className="sb__group-heading">
          {Icon && (
            <span className="sb__group-icon" aria-hidden="true">
              <Icon />
            </span>
          )}
          <span>{section.label}</span>
        </div>
        <ul className="sb__items">
          {section.items.map((item) => (
            <li key={item.id || item.href}>
              <ItemLink item={item} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  // kind === 'collapsible'
  return <CollapsibleSection section={section} />;
}

function CollapsibleSection({ section }) {
  const Icon = section.icon;
  const [open, setOpen] = useState(() => readOpenFromStorage(section.id, section.defaultOpen));

  const toggle = useCallback((e) => {
    // Honour the native <details> toggle semantics — onToggle fires
    // *after* the change, so just persist whatever the element says.
    const nextOpen = e?.target?.open ?? !open;
    setOpen(nextOpen);
    try { localStorage.setItem(`spotlight.nav.section.${section.id}`, nextOpen ? '1' : '0'); }
    catch { /* private mode */ }
  }, [open, section.id]);

  // Auto-open when the current route is inside the section — saves the
  // operator a click after navigating from elsewhere.
  const location = useLocation();
  const containsActive = useMemo(
    () => sectionContainsPath(section, location.pathname),
    [section, location.pathname],
  );
  useEffect(() => {
    if (containsActive && !open) {
      setOpen(true);
      // Don't persist — auto-open is contextual; the persisted
      // preference reflects the operator's manual toggles only.
    }
  }, [containsActive, open]);

  return (
    <details
      className={`sb__collapsible${containsActive ? ' sb__collapsible--active' : ''}${section.scope === 'project' ? ' sb__collapsible--scope-project' : ''}`}
      open={open}
      onToggle={toggle}
    >
      <summary className="sb__collapsible-summary">
        <span className="sb__collapsible-icon"><Icon /></span>
        <span className="sb__collapsible-label">{section.label}</span>
        <span className="sb__collapsible-caret" aria-hidden="true"><ChevronDownIcon /></span>
      </summary>
      <ul className="sb__items sb__items--nested">
        {section.items.map((item) => (
          <li key={item.id || item.href}>
            <ItemLink item={item} />
          </li>
        ))}
      </ul>
    </details>
  );
}

function SoloLink({ item }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.href}
      end={item.end}
      data-ui-name={item.id ? `sidebar-${item.id}` : undefined}
      className={({ isActive }) => `sb__solo${isActive ? ' sb__solo--active' : ''}`}
    >
      <span className="sb__solo-icon"><Icon /></span>
      <span className="sb__solo-label">{item.label}</span>
      {item.badge > 0 && (
        <span className={`sb__badge sb__badge--${item.badgeTone || 'urgent'}`}>
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </NavLink>
  );
}

function ItemLink({ item }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.href}
      end={item.end}
      data-ui-name={item.id ? `sidebar-${item.id}` : undefined}
      className={({ isActive }) => `sb__item${isActive ? ' sb__item--active' : ''}`}
    >
      {Icon && <span className="sb__item-icon"><Icon /></span>}
      <span className="sb__item-label">{item.label}</span>
      {item.badge > 0 && (
        <span className={`sb__badge sb__badge--${item.badgeTone || 'normal'}`}>
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </NavLink>
  );
}

// ───────── config → resolved tree

function buildSections({ projectSlug, badges }) {
  const out = [];
  for (const section of NAV_CONFIG) {
    if (section.requiresProject && !projectSlug) continue;
    if (section.kind === 'solo') {
      const item = resolveItem(section.item, { projectSlug, badges });
      if (!item) continue;
      out.push({ ...section, item });
      continue;
    }
    const items = (section.items || [])
      .map((it) => resolveItem(it, { projectSlug, badges }))
      .filter(Boolean);
    if (items.length === 0) continue;
    out.push({ ...section, items });
  }
  return out;
}

function resolveItem(item, { projectSlug, badges }) {
  if (item.requiresProject && !projectSlug) return null;
  const href = typeof item.href === 'function' ? item.href({ projectSlug }) : item.href;
  if (!href) return null;
  return {
    ...item,
    href,
    badge: item.badgeKey ? (badges[item.badgeKey] || 0) : 0,
  };
}

function sectionContainsPath(section, pathname) {
  const items = section.items || (section.item ? [section.item] : []);
  for (const item of items) {
    if (!item?.href) continue;
    if (pathStartsWith(pathname, item.href)) return true;
  }
  return false;
}

function pathStartsWith(pathname, href) {
  if (href === '/') return pathname === '/';
  if (pathname === href) return true;
  return pathname.startsWith(href + '/');
}

function readOpenFromStorage(sectionId, fallback) {
  try {
    const v = localStorage.getItem(`spotlight.nav.section.${sectionId}`);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch { /* private mode */ }
  return !!fallback;
}

// ───────── icons (Lucide-style, inline SVG)

function makeIcon(paths) {
  return function Icon() {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {paths}
      </svg>
    );
  };
}

const InboxIcon = makeIcon(
  <>
    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
  </>,
);
const RouteIcon = makeIcon(
  <>
    <circle cx="6" cy="19" r="3" />
    <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
    <circle cx="18" cy="5" r="3" />
  </>,
);
const MessageSquareIcon = makeIcon(
  <>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
  </>,
);
const ListChecksIcon = makeIcon(
  <>
    <path d="m3 17 2 2 4-4" />
    <path d="m3 7 2 2 4-4" />
    <path d="M13 6h8" />
    <path d="M13 12h8" />
    <path d="M13 18h8" />
  </>,
);
const BookOpenIcon = makeIcon(
  <>
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2Z" />
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7Z" />
  </>,
);
const PaletteIcon = makeIcon(
  <>
    <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
    <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
    <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
    <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.9 0 1.5-.6 1.5-1.5 0-.4-.2-.8-.4-1.1-.3-.3-.5-.7-.5-1.1 0-.9.6-1.5 1.5-1.5H16c3.3 0 6-2.7 6-6 0-4.9-4.5-9-10-9Z" />
  </>,
);
const BarChartIcon = makeIcon(
  <>
    <path d="M3 3v18h18" />
    <path d="M8 17V9" />
    <path d="M12 17v-5" />
    <path d="M16 17v-9" />
  </>,
);
const ActivityIcon = makeIcon(
  <>
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </>,
);
const ThumbsUpIcon = makeIcon(
  <>
    <path d="M7 10v12" />
    <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7" />
    <path d="M7 10H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" />
    <path d="M15 5.88a3 3 0 0 0-3-3l-1 5" />
  </>,
);
const SettingsIcon = makeIcon(
  <>
    <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2Z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);
const CodeIcon = makeIcon(
  <>
    <path d="m18 16 4-4-4-4" />
    <path d="m6 8-4 4 4 4" />
    <path d="m14.5 4-5 16" />
  </>,
);
const GlobeIcon = makeIcon(
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
    <path d="M2 12h20" />
  </>,
);
const ShieldCheckIcon = makeIcon(
  <>
    <path d="M20 13c0 5-3.5 7.5-8 9-4.5-1.5-8-4-8-9V5l8-3 8 3Z" />
    <path d="m9 12 2 2 4-4" />
  </>,
);
const WebhookIcon = makeIcon(
  <>
    <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 1 1 8.4 13.6" />
    <path d="m12 7-3 5 1.5 2.5" />
    <path d="M17 14a4 4 0 1 1 .3 7.97l-3.81-7" />
  </>,
);
const PlugIcon = makeIcon(
  <>
    <path d="M12 22v-5" />
    <path d="M9 8V2" />
    <path d="M15 8V2" />
    <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
  </>,
);
const PackageIcon = makeIcon(
  <>
    <path d="m7.5 4.27 9 5.15" />
    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="M3.3 7 12 12l8.7-5" />
    <path d="M12 22V12" />
  </>,
);
// Lucide `bookmark` — Setup → Bookmarklet (card 063).  Slim ribbon
// glyph matches Lucide's own `bookmark` shape so it reads
// "bookmark bar" at a glance.
const BookmarkIcon = makeIcon(
  <>
    <path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16Z" />
  </>,
);
const BuildingIcon = makeIcon(
  <>
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M9 22v-4h6v4" />
    <path d="M8 6h.01" />
    <path d="M16 6h.01" />
    <path d="M8 10h.01" />
    <path d="M16 10h.01" />
    <path d="M8 14h.01" />
    <path d="M16 14h.01" />
  </>,
);
const FolderIcon = makeIcon(
  <>
    <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9L8.93 3.9a2 2 0 0 0-1.66-.9H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
  </>,
);
const UsersIcon = makeIcon(
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </>,
);
const KeyRoundIcon = makeIcon(
  <>
    <path d="M2 18a6 6 0 0 0 11.15 3l2.85-2.85V15h3v-3l3-3-4-4-7.15 7.15A6 6 0 0 0 2 18Z" />
    <circle cx="7" cy="18" r="1" fill="currentColor" />
  </>,
);
const AtSignIcon = makeIcon(
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
  </>,
);
const ScrollTextIcon = makeIcon(
  <>
    <path d="M15 12h-5" />
    <path d="M15 8h-5" />
    <path d="M19 17V5a2 2 0 0 0-2-2H4" />
    <path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 0 0-2-2 2 2 0 0 0-2 2v3h5" />
  </>,
);
const WalletIcon = makeIcon(
  <>
    <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2" />
    <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
    <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
  </>,
);
const SlidersIcon = makeIcon(
  <>
    <line x1="21" y1="4" x2="14" y2="4" />
    <line x1="10" y1="4" x2="3" y2="4" />
    <line x1="21" y1="12" x2="12" y2="12" />
    <line x1="8" y1="12" x2="3" y2="12" />
    <line x1="21" y1="20" x2="16" y2="20" />
    <line x1="12" y1="20" x2="3" y2="20" />
    <line x1="14" y1="2" x2="14" y2="6" />
    <line x1="8" y1="10" x2="8" y2="14" />
    <line x1="16" y1="18" x2="16" y2="22" />
  </>,
);
const ChevronDownIcon = makeIcon(
  <>
    <path d="m6 9 6 6 6-6" />
  </>,
);
const SearchIcon = makeIcon(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </>,
);

// ───────── nav config — declarative IA, easy to edit
//
// Sections come in three flavours:
//   kind: 'solo'        — single link, no group heading.
//   kind: 'flat'        — non-collapsing group with a heading row.
//   kind: 'collapsible' — <details> wrapper, persisted to localStorage.

const NAV_CONFIG = [
  // INBOX — sits above everything else.  Reactive work.  Always
  // visible regardless of project scope because the inbox spans
  // every project the operator can see.
  // Card 096 — sidebar label, URL slug, and HeaderBar breadcrumb
  // must agree on ONE name per concept.  Inbox is the tenant-wide
  // Pinpoint inbox; the URL is /inbox to match the label (was
  // /help, which clashed with the project-scoped help-widget config
  // and produced a "Help inbox" breadcrumb that nobody read).
  {
    id: 'inbox',
    kind: 'solo',
    item: {
      id: 'inbox',
      label: 'Inbox',
      icon: InboxIcon,
      href: '/inbox',
      end: true,
      badgeKey: 'helpUnread',
      badgeTone: 'urgent',
    },
  },

  // AUTHORING — content the operator builds.  Project-scoped today,
  // so the whole section hides when no project is in scope.  The
  // breadcrumb in HeaderBar nudges the user to pick a project
  // before they land here.
  {
    id: 'authoring',
    kind: 'flat',
    label: 'Authoring',
    icon: PaletteIcon,
    scope: 'project',
    requiresProject: true,
    items: [
      {
        id: 'tours',
        label: 'Tours',
        icon: RouteIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/tours`,
      },
      {
        id: 'content',
        label: 'Content',
        icon: MessageSquareIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/content`,
      },
      {
        id: 'checklists',
        label: 'Checklists',
        icon: ListChecksIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/checklists`,
      },
      {
        id: 'knowledge-base',
        label: 'Knowledge base',
        icon: BookOpenIcon,
        href: '/knowledge-base',
      },
      {
        id: 'themes',
        label: 'Themes',
        icon: PaletteIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/themes`,
      },
    ],
  },

  // INSIGHTS — analytical surfaces.  Mostly project-scoped; help
  // feedback is tenant-wide.
  {
    id: 'insights',
    kind: 'flat',
    label: 'Insights',
    icon: BarChartIcon,
    scope: 'project',
    items: [
      {
        id: 'tour-analytics',
        label: 'Tour analytics',
        icon: BarChartIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/analytics`,
      },
      {
        id: 'user-progress',
        label: 'User progress',
        icon: ActivityIcon,
        href: '/users',
      },
      {
        id: 'help-feedback',
        label: 'Help feedback',
        icon: ThumbsUpIcon,
        href: '/help/feedback',
      },
    ],
  },

  // SETUP — one-time configuration, collapsible.  Defaults open
  // during onboarding when nothing is set up yet; the user collapses
  // it once they're past install and the choice persists.
  {
    id: 'setup',
    kind: 'collapsible',
    label: 'Setup',
    icon: SettingsIcon,
    scope: 'project',
    defaultOpen: true,
    items: [
      {
        id: 'install',
        label: 'Install',
        icon: CodeIcon,
        href: ({ projectSlug }) =>
          projectSlug ? `/projects/${projectSlug}/install` : '/install',
      },
      // Card 063 — Bookmarklet sits between Install and Sites:
      // Install is the first-time setup the customer does on their
      // own page; the bookmarklet is the day-to-day "boot Spotlight
      // in admin mode on any page" tool the operator drags onto
      // their bookmark bar.  Project-scoped only — without a project
      // in scope there's no key to bundle into the URL.
      {
        id: 'bookmarklet',
        label: 'Bookmarklet',
        icon: BookmarkIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/bookmarklet`,
      },
      {
        id: 'sites',
        label: 'Sites',
        icon: GlobeIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/sites`,
      },
      {
        id: 'authentication',
        label: 'Authentication',
        icon: ShieldCheckIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/authentication`,
      },
      {
        id: 'webhooks',
        label: 'Webhooks',
        icon: WebhookIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/webhooks`,
      },
      // Card 088 — Pinpoint help-widget config has lived under
      // /projects/:slug/help since card 049 but was never linked from
      // anywhere.  Setup group is the right home — enable/disable +
      // mount + position + quickPrompts are all one-time wiring.
      {
        id: 'project-help-config',
        label: 'Help widget',
        icon: MessageSquareIcon,
        requiresProject: true,
        href: ({ projectSlug }) => `/projects/${projectSlug}/help-widget`,
      },
      {
        id: 'sdk-version',
        label: 'SDK version',
        icon: PackageIcon,
        href: '/sdk-version',
      },
    ],
  },

  // TENANT ADMIN — collapsed by default.  Heavyweight admin
  // surfaces an operator only needs occasionally.
  {
    id: 'tenant-admin',
    kind: 'collapsible',
    label: 'Tenant admin',
    icon: BuildingIcon,
    defaultOpen: false,
    items: [
      {
        id: 'projects',
        label: 'Projects',
        icon: FolderIcon,
        href: '/projects',
        end: true,
      },
      {
        id: 'members',
        label: 'Members',
        icon: UsersIcon,
        href: '/members',
        badgeKey: 'pendingAccess',
        badgeTone: 'primary',
      },
      {
        id: 'tenant-api-keys',
        label: 'API keys',
        icon: KeyRoundIcon,
        // Card 053 — `/keys` is now the tenant-wide admin view of
        // every API key on the tenant subject to role hierarchy.
        // The caller's own personal keys live on `/my-keys`, linked
        // from the UserMenu in the header bar.
        href: '/keys',
      },
      {
        id: 'email-domains',
        label: 'Email domains',
        icon: AtSignIcon,
        href: '/email-domains',
      },
      {
        id: 'tenant-webhooks',
        label: 'Tenant webhooks',
        icon: WebhookIcon,
        href: '/webhooks',
      },
      {
        // Card spotlight-055 phase 1 — Slack mirror today, more
        // integrations land in this same surface in later phases.
        id: 'integrations',
        label: 'Integrations',
        icon: PlugIcon,
        href: '/integrations',
      },
      {
        // Card spotlight-081 — OAuth-minted MCP tokens (Claude /
        // Codex / Cursor) get a sister item under Integrations so
        // the operator can reauthorize / revoke a specific app
        // without sifting through the catch-all /keys list.
        id: 'connected-apps',
        label: 'Connected apps',
        icon: PlugIcon,
        href: '/integrations/connected',
      },
      {
        id: 'audit-log',
        label: 'Audit log',
        icon: ScrollTextIcon,
        href: '/audit',
      },
      {
        id: 'billing',
        label: 'Billing',
        icon: WalletIcon,
        href: '/billing',
      },
      {
        id: 'tenant-settings',
        label: 'Tenant settings',
        icon: SlidersIcon,
        href: '/settings',
      },
    ],
  },
];

const SIDEBAR_CSS = `
  .sb {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 16px 12px;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }

  .sb__search {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 10px;
    border-radius: var(--r-md, 10px);
    background: var(--surface-1, #f8fafc);
    color: var(--text-3, #64748b);
    border: 1px solid var(--border-1, #e2e8f0);
    cursor: text;
    font-size: 13px;
    font-weight: 500;
    width: 100%;
    text-align: left;
    transition: background 120ms ease, border-color 120ms ease;
  }
  .sb__search:hover,
  .sb__search:focus-visible {
    background: var(--surface-0, #ffffff);
    border-color: var(--brand-300, #c7d2fe);
    color: var(--text-1, #0f172a);
    outline: none;
  }
  .sb__search-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--text-3, #64748b);
    flex-shrink: 0;
  }
  .sb__search-icon svg { width: 16px; height: 16px; }
  .sb__search-label { flex: 1; min-width: 0; }
  .sb__search-kbd {
    font-family: inherit;
    font-size: 10.5px;
    font-weight: 600;
    color: var(--text-3, #64748b);
    background: var(--surface-0, #ffffff);
    border: 1px solid var(--border-1, #e2e8f0);
    padding: 1px 6px;
    border-radius: 4px;
    flex-shrink: 0;
  }

  .sb__solo-wrap { display: flex; flex-direction: column; gap: 2px; }

  .sb__solo {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: var(--r-md, 10px);
    color: var(--text-1, #0f172a);
    text-decoration: none;
    font-size: 13.5px;
    font-weight: 600;
    transition: background 120ms ease, color 120ms ease;
  }
  .sb__solo:hover {
    background: var(--surface-1, #f8fafc);
  }
  .sb__solo--active {
    background: var(--brand-50, #eef2ff);
    color: var(--brand-700, #4338ca);
  }
  .sb__solo--active:hover { background: var(--brand-50, #eef2ff); }

  .sb__solo-icon {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    color: var(--text-3, #64748b);
  }
  .sb__solo--active .sb__solo-icon { color: var(--brand-600, #4f46e5); }
  .sb__solo-label { flex: 1; min-width: 0; }

  .sb__group { display: flex; flex-direction: column; gap: 2px; }

  .sb__group-heading {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-3, #64748b);
    padding: 4px 10px 6px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  /* Card 103 — project-scoped groups get a left-edge brand accent so
     operators can read at a glance "this section changes per project"
     vs "this section is tenant-wide". */
  .sb__group--scope-project,
  .sb__collapsible--scope-project {
    border-left: 2px solid var(--brand-100, #e0e7ff);
    padding-left: 6px;
    margin-left: 2px;
  }
  .sb__group-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--text-3, #64748b);
    opacity: 0.85;
  }
  .sb__group-icon svg { width: 14px; height: 14px; }

  .sb__items {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .sb__items--nested {
    padding: 4px 0 4px 6px;
    margin: 2px 0 0;
  }

  .sb__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 10px;
    border-radius: var(--r-md, 10px);
    color: var(--text-2, #334155);
    text-decoration: none;
    font-size: 13px;
    font-weight: 500;
    transition: background 120ms ease, color 120ms ease;
  }
  .sb__item:hover {
    background: var(--surface-1, #f8fafc);
    color: var(--text-1, #0f172a);
  }
  .sb__item--active {
    background: var(--brand-50, #eef2ff);
    color: var(--brand-700, #4338ca);
  }
  .sb__item--active:hover { background: var(--brand-50, #eef2ff); }
  .sb__item-icon {
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    color: var(--text-3, #64748b);
  }
  .sb__item--active > .sb__item-icon { color: var(--brand-600, #4f46e5); }
  .sb__item-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .sb__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    border-radius: 9999px;
    font-size: 10.5px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
  }
  .sb__badge--urgent  { background: var(--danger-600, #dc2626); }
  .sb__badge--primary { background: var(--brand-600, #4f46e5); }
  .sb__badge--normal  { background: var(--text-3, #64748b); }

  /* Collapsible section (Setup / Tenant admin) */
  .sb__collapsible {
    display: flex;
    flex-direction: column;
    border-radius: var(--r-md, 10px);
  }
  .sb__collapsible-summary {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: var(--r-md, 10px);
    color: var(--text-1, #0f172a);
    cursor: pointer;
    font-size: 13.5px;
    font-weight: 600;
    list-style: none;
    user-select: none;
    transition: background 120ms ease;
  }
  .sb__collapsible-summary::-webkit-details-marker { display: none; }
  .sb__collapsible-summary:hover { background: var(--surface-1, #f8fafc); }
  .sb__collapsible--active > .sb__collapsible-summary {
    color: var(--brand-700, #4338ca);
  }
  .sb__collapsible-icon {
    width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    color: var(--text-3, #64748b);
  }
  .sb__collapsible--active .sb__collapsible-icon { color: var(--brand-600, #4f46e5); }
  .sb__collapsible-label {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sb__collapsible-caret {
    color: var(--text-3, #64748b);
    display: inline-flex;
    align-items: center;
    transition: transform 160ms ease;
  }
  .sb__collapsible[open] > .sb__collapsible-summary > .sb__collapsible-caret {
    transform: rotate(180deg);
  }
  @media (prefers-reduced-motion: reduce) {
    .sb__collapsible-caret { transition: none; }
  }
`;
